import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { setAnonDiag } from "@/lib/anonDiag";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import QuestionCard from "@/components/QuestionCard";
import MathText from "@/components/MathText";
import { APP, GRADE_OPTIONS, gradeLabel, gradeShort } from "@/config";
import { ArrowRight, Sparkles, Heart, Lock, CheckCircle2, ShieldCheck } from "lucide-react";

const CHEERS = ["Nice effort! 🌱", "Great — keep going!", "You're doing brilliantly.", "Best guesses are perfect here.", "Love that thinking!"];

export default function PublicDiagnostic() {
  const nav = useNavigate();
  const [stage, setStage] = useState("grade"); // grade | quiz | result
  const [grade, setGrade] = useState(5);
  const [session, setSession] = useState(null);
  const [item, setItem] = useState(null);
  const [answer, setAnswer] = useState("");
  const [answered, setAnswered] = useState(0);
  const [total, setTotal] = useState(8);
  const [busy, setBusy] = useState(false);
  const [cheer, setCheer] = useState("");
  const [result, setResult] = useState(null);

  const begin = async () => {
    setBusy(true);
    track("diagnostic_grade_selected", { grade });
    try {
      const { data } = await api.post("/public/diagnostic/start", { grade });
      setSession(data.session_id);
      setAnonDiag(data.session_id);
      setItem(data.item);
      setTotal(data.cap || 8);
      setStage("quiz");
      track("diagnostic_started", { grade, source: "public" });
    } catch {
      toast.error("Couldn't start the diagnostic. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!answer && item.type !== "mcq") return;
    setBusy(true);
    try {
      const { data } = await api.post(`/public/diagnostic/${session}/answer`, {
        skill_id: item.skill_id, item_id: item.item_id, answer,
      });
      if (data.done) {
        setResult(data);
        setStage("result");
        track("diagnostic_completed", { grade, gaps: data.gap_count, answered: data.answered });
      } else {
        setItem(data.item);
        setAnswered(data.answered);
        setAnswer("");
        setCheer(CHEERS[data.answered % CHEERS.length]);
      }
    } finally {
      setBusy(false);
    }
  };

  const unlock = () => {
    track("diagnostic_unlock_click", { grade, gaps: result?.gap_count });
    nav("/auth?mode=register&from=diagnostic");
  };

  return (
    <div className="band-3-5 min-h-screen bg-[var(--ns-bg)]">
      <header className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
        <button className="flex items-center gap-2" onClick={() => nav("/")} data-testid="diag-home-btn">
          <div className="w-9 h-9 rounded-xl bg-[var(--ns-primary)] text-white grid place-items-center font-head font-bold">N</div>
          <span className="font-head text-lg font-bold text-[var(--ns-text)]">{APP.name}</span>
        </button>
        <Button variant="ghost" data-testid="diag-login-btn" onClick={() => nav("/auth?mode=login")}>Log in</Button>
      </header>

      <main className="max-w-xl mx-auto px-5 pb-16">
        {stage === "grade" && (
          <GradeStage grade={grade} setGrade={setGrade} onBegin={begin} busy={busy} />
        )}

        {stage === "quiz" && item && (
          <QuizStage
            item={item} answer={answer} setAnswer={setAnswer} submit={submit}
            busy={busy} answered={answered} total={total} cheer={cheer} grade={grade}
          />
        )}

        {stage === "quiz" && !item && <Spinner label="Preparing your diagnostic" />}

        {stage === "result" && result && (
          <ResultStage result={result} onUnlock={unlock} />
        )}
      </main>
    </div>
  );
}

function GradeStage({ grade, setGrade, onBegin, busy }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="diag-grade-stage">
      <Card className="p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl grid place-items-center mb-4" style={{ background: "var(--ns-primary-light)", color: "var(--ns-primary)" }}>
          <Sparkles size={30} />
        </div>
        <h1 className="font-head text-3xl font-bold">Find your child's math gaps</h1>
        <p className="text-[var(--ns-muted)] mt-2 max-w-sm mx-auto">
          A short, adaptive check — about 8 questions, no account needed. First, which grade is your child in?
        </p>

        <div className="mt-6 grid grid-cols-3 gap-2 max-w-sm mx-auto" data-testid="diag-grade-grid">
          {GRADE_OPTIONS.map((g) => (
            <button
              key={g}
              data-testid={`diag-grade-${g}`}
              onClick={() => setGrade(g)}
              className="rounded-[var(--ns-radius)] border-2 py-3 text-sm font-semibold transition-colors"
              style={{
                borderColor: grade === g ? "var(--ns-primary)" : "var(--ns-border)",
                background: grade === g ? "var(--ns-primary-light)" : "transparent",
                color: grade === g ? "var(--ns-primary-hover)" : "var(--ns-muted)",
              }}
            >
              {gradeShort(g)}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-2 text-sm text-left max-w-xs mx-auto">
          {["No timer, no pressure", "About 5 minutes", "See the #1 gap for free"].map((t) => (
            <div key={t} className="flex items-center gap-2 text-[var(--ns-muted)]"><Heart size={15} className="text-[#D96B43]" /> {t}</div>
          ))}
        </div>

        <Button className="mt-7 mx-auto px-8" data-testid="diag-begin-btn" disabled={busy} onClick={onBegin}>
          {busy ? "Starting…" : <>Start the free check <ArrowRight size={18} /></>}
        </Button>
        <p className="mt-3 text-xs text-[var(--ns-subtle)] flex items-center justify-center gap-1">
          <ShieldCheck size={13} /> We don't ask for your email to start.
        </p>
      </Card>
    </motion.div>
  );
}

function QuizStage({ item, answer, setAnswer, submit, busy, answered, total, cheer, grade }) {
  const progress = Math.min(100, Math.round((answered / Math.max(total, 1)) * 100));
  return (
    <div data-testid="diag-quiz-stage">
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-[var(--ns-muted)] mb-2">
          <span className="font-semibold">{gradeLabel(grade)} check · {item.skill_name}</span>
          <span>{answered} / {total}</span>
        </div>
        <div className="h-2 rounded-full bg-[var(--ns-surface-subtle)] overflow-hidden">
          <div className="h-full bg-[var(--ns-primary)] transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        {cheer && (
          <motion.div key={cheer + answered} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm font-semibold text-[var(--ns-primary)]" data-testid="diag-cheer">{cheer}</motion.div>
        )}
      </div>
      <Card className="p-6">
        <QuestionCard item={item} value={answer} onChange={setAnswer} disabled={busy} />
        <Button className="mt-6 w-full" data-testid="diag-submit-btn"
          disabled={busy || (!answer && item.type !== "mcq") || (item.type === "mcq" && !answer)} onClick={submit}>
          {busy ? "…" : "Submit"} <ArrowRight size={18} />
        </Button>
        <p className="text-xs text-center text-[var(--ns-subtle)] mt-3">Not sure? A best guess is perfect — this just helps us place your child.</p>
      </Card>
    </div>
  );
}

function ResultStage({ result, onUnlock }) {
  const top = result.top_gap;
  const lockedCount = result.locked_gaps?.length || 0;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="diag-result-stage">
      <Card className="p-7 text-center mb-5">
        <CheckCircle2 size={44} className="mx-auto text-[#2A8555]" />
        <h1 className="font-head text-2xl font-bold mt-3">Your gap map is ready</h1>
        <p className="text-[var(--ns-muted)] mt-2">
          From {result.answered} questions we found{" "}
          <b>{result.gap_count} skill{result.gap_count === 1 ? "" : "s"}</b> to focus on, with foundations first.
        </p>
      </Card>

      {top ? (
        <>
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2">Your child's #1 gap</div>
          <Card className="p-5 mb-4" data-testid="diag-top-gap">
            <div className="flex items-center justify-between">
              <span className="font-head font-semibold text-lg">{top.name}</span>
              <Pill tone="gap">{gradeShort(top.grade)} · {top.strand}</Pill>
            </div>
            <p className="text-sm text-[var(--ns-muted)] mt-2"><b>Why this matters next:</b> <MathText text={top.why_it_matters} /></p>
          </Card>

          {lockedCount > 0 && (
            <div className="relative mb-6" data-testid="diag-locked-gaps">
              <div className="grid gap-3" aria-hidden="true">
                {result.locked_gaps.slice(0, 3).map((g, i) => (
                  <Card key={i} className="p-4 select-none" style={{ filter: "blur(5px)" }}>
                    <div className="flex items-center justify-between">
                      <span className="font-head font-semibold">Skill in {g.strand}</span>
                      <Pill tone="gap">{gradeShort(g.grade)} · {g.strand}</Pill>
                    </div>
                    <p className="text-sm text-[var(--ns-muted)] mt-1">Why this matters next: unlock to see the full explanation and plan.</p>
                  </Card>
                ))}
              </div>
              <div className="absolute inset-0 grid place-items-center">
                <div className="flex items-center gap-2 rounded-full bg-[var(--ns-surface)] px-4 py-2 shadow-md border border-[var(--ns-border)]">
                  <Lock size={16} className="text-[var(--ns-primary)]" />
                  <span className="text-sm font-semibold">+{lockedCount} more gap{lockedCount === 1 ? "" : "s"} locked</span>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card className="p-6 text-center text-[var(--ns-muted)] mb-6" data-testid="diag-no-gaps">
          No gaps on these skills — your child is ready for grade-level challenges! Create a free account to build a stretch plan.
        </Card>
      )}

      <Card className="p-6 text-center" style={{ background: "var(--ns-primary-light)" }}>
        <h2 className="font-head text-xl font-bold">Unlock the full plan — free</h2>
        <p className="text-sm text-[var(--ns-muted)] mt-1 max-w-sm mx-auto">
          Create a free parent account to see every gap, the step-by-step mastery path, and the Socratic tutor.
        </p>
        <Button className="mt-4 mx-auto px-8" variant="accent" data-testid="diag-unlock-btn" onClick={onUnlock}>
          Create free account to unlock <ArrowRight size={18} />
        </Button>
        <p className="mt-3 text-xs text-[var(--ns-subtle)]">We'll build this plan onto your child's profile automatically.</p>
      </Card>
    </motion.div>
  );
}
